import React, { useState, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import IntentBar from '@/components/layout/IntentBar'
import Sidebar from '@/components/layout/Sidebar'
import { MobileNav } from '@/components/layout/MobileNav'
import { CommandBar } from '@/features/shared/CommandBar'
import { PreviewPanel } from '@/features/shared/PreviewPanel'
import { useNavigationStore } from '@/stores/navigationStore'
import { ToastProvider } from '@/components/ui/toast'
import { PageTransition } from '@/components/ui/animations'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Sheet,
  SheetContent,
} from '@/components/ui/sheet'

// Sidebar width constants (should match Sidebar.tsx)
const SIDEBAR_MIN_WIDTH = 72
const SIDEBAR_DEFAULT_WIDTH = 288
const SIDEBAR_STORAGE_KEY = 'rsb-sidebar-width'
const SIDEBAR_COLLAPSED_KEY = 'rsb-sidebar-collapsed'

interface AppShellProps {
  children: React.ReactNode
}

export default function AppShell({ children }: AppShellProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [intentBarFocused, setIntentBarFocused] = useState(false)
  const [showShortcuts, setShowShortcuts] = useState(false)
  const location = useLocation()
  const { navigate: navStoreNavigate } = useNavigationStore()

  // Track sidebar dimensions for content offset
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    if (typeof window !== 'undefined') {
      const collapsed = localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'
      if (collapsed) return SIDEBAR_MIN_WIDTH
      const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY)
      return stored ? parseInt(stored, 10) : SIDEBAR_DEFAULT_WIDTH
    }
    return SIDEBAR_DEFAULT_WIDTH
  })

  // Listen for sidebar width changes
  useEffect(() => {
    const handleStorageChange = () => {
      const collapsed = localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'
      if (collapsed) {
        setSidebarWidth(SIDEBAR_MIN_WIDTH)
      } else {
        const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY)
        setSidebarWidth(stored ? parseInt(stored, 10) : SIDEBAR_DEFAULT_WIDTH)
      }
    }

    // Check periodically for changes (localStorage events only fire across tabs)
    const interval = setInterval(handleStorageChange, 100)
    return () => clearInterval(interval)
  }, [])

  // Track navigation in store
  useEffect(() => {
    const pathParts = location.pathname.split('/').filter(Boolean)
    const pathSegment = pathParts[0] || ''

    // Map URL paths to surfaces
    const surfaceMap: Record<string, 'pulse' | 'navigator' | 'graph' | 'canvas' | 'focus'> = {
      '': 'pulse',
      'pulse': 'pulse',
      'library': 'navigator',
      'constellation': 'graph',
      'canvas': 'canvas',
      'dive': 'focus',
      'settings': 'pulse'
    }

    const surfaceLabels: Record<string, string> = {
      '': 'Pulse',
      'pulse': 'Pulse',
      'library': 'Library',
      'constellation': 'Constellation',
      'canvas': 'Canvas',
      'dive': 'Focus',
      'settings': 'Settings'
    }

    navStoreNavigate({
      surface: surfaceMap[pathSegment] || 'pulse',
      timestamp: Date.now(),
      title: surfaceLabels[pathSegment] || 'Pulse'
    })
  }, [location.pathname, navStoreNavigate])

  const toggleSidebar = useCallback(() => {
    setSidebarOpen(prev => !prev)
  }, [])

  const closeSidebar = useCallback(() => {
    setSidebarOpen(false)
  }, [])

  // Global keyboard shortcuts (navigation shortcuts now handled by CommandBar)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't intercept when typing in inputs
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return
      }

      if (e.key === '?' && !e.metaKey && !e.ctrlKey) {
        e.preventDefault()
        setShowShortcuts(prev => !prev)
      }
      if (e.key === 'Escape' && showShortcuts) {
        setShowShortcuts(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [showShortcuts])

  // Calculate content padding for large screens
  const contentPaddingLeft = typeof window !== 'undefined' && window.innerWidth >= 1024
    ? `${sidebarWidth + 32}px`
    : '1rem'

  return (
    <ToastProvider position="top-right" maxToasts={5}>
      <div className="min-h-screen bg-rsb-bg selection:bg-rsb-accent/30 text-white overflow-x-hidden">

        {/* Skip to main content link - accessibility */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-rsb-accent focus:text-white focus:rounded-lg focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-rsb-bg"
        >
          Ir para conteúdo principal
        </a>

        {/* Background Ambience with subtle animation */}
        <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
          <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] rounded-full bg-rsb-accent/5 blur-[120px] animate-pulse-slow" />
          <div
            className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] rounded-full bg-domain-business/5 blur-[120px] animate-pulse-slow"
            style={{ animationDelay: '2s' }}
          />
        </div>

        {/* Intent Bar Container - Floating & Centered */}
        <div className={`
          fixed top-0 left-0 right-0 z-50 pointer-events-none transition-all duration-500
          ${intentBarFocused ? 'backdrop-blur-sm bg-black/20' : ''}
        `}>
          {/* Child needs pointer-events-auto */}
          <div className="pointer-events-auto w-full">
            <IntentBar
              onToggleSidebar={toggleSidebar}
              sidebarOpen={sidebarOpen}
              onFocusChange={setIntentBarFocused}
            />
          </div>
        </div>

        {/* Sidebar - Floating Dock */}
        <Sidebar
          isOpen={sidebarOpen}
          onClose={closeSidebar}
        />

        {/* Main Content Area - Dynamic padding based on sidebar width */}
        <div
          className="relative z-10 min-h-screen pt-32 px-4 pb-24 md:pb-8 transition-all duration-300 lg:pr-8"
          style={{ paddingLeft: contentPaddingLeft }}
        >

          {/* Mobile Sidebar Backdrop via Sheet */}
          <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
            <SheetContent
              side="left"
              className="w-72 p-0 bg-transparent border-0 shadow-none lg:hidden [&>button]:hidden"
            >
              {/* Sheet provides the backdrop overlay; Sidebar renders inside */}
            </SheetContent>
          </Sheet>

          {/* Content Wrapper with Page Transition */}
          <main id="main-content" className="w-full max-w-7xl mx-auto" tabIndex={-1}>
            <PageTransition key={location.pathname} animation="fade" duration={200}>
              {children}
            </PageTransition>
          </main>
        </div>

        {/* Mobile Bottom Navigation */}
        <MobileNav />

        {/* Command Bar (Cmd+K) */}
        <CommandBar />

        {/* Preview Panel */}
        <PreviewPanel />

        {/* Keyboard Shortcuts Modal */}
        <Dialog open={showShortcuts} onOpenChange={setShowShortcuts}>
          <DialogContent className="glass-panel border-white/10 bg-[#0A0A0F]/90 backdrop-blur-xl shadow-2xl max-w-md animate-in fade-in duration-200">
            <DialogHeader>
              <DialogTitle className="text-xl font-display font-bold text-white tracking-tight">
                Keyboard Shortcuts
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-6">
              <div>
                <div className="text-[10px] font-bold text-rsb-muted uppercase tracking-widest mb-3">Navigation</div>
                <div className="space-y-2">
                  {[
                    ['Pulse', 'Cmd 1'],
                    ['Library', 'Cmd 2'],
                    ['Constellation', 'Cmd 3'],
                    ['Canvas', 'Cmd 4'],
                    ['Settings', 'Cmd ,'],
                  ].map(([label, shortcut]) => (
                    <div key={shortcut} className="flex justify-between items-center group hover:bg-white/5 px-2 py-1 rounded-lg transition-colors">
                      <span className="text-rsb-text-secondary group-hover:text-white transition-colors">{label}</span>
                      <kbd className="px-2 py-1 bg-white/5 border border-white/10 rounded-md text-xs font-mono text-rsb-muted group-hover:text-white group-hover:border-white/20 transition-all">{shortcut}</kbd>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-[10px] font-bold text-rsb-muted uppercase tracking-widest mb-3">Actions</div>
                <div className="space-y-2">
                  {[
                    ['Search', 'Cmd K'],
                    ['Toggle Sidebar', 'Cmd B'],
                    ['Help', '?'],
                    ['Close', 'Esc'],
                  ].map(([label, shortcut]) => (
                    <div key={shortcut} className="flex justify-between items-center group hover:bg-white/5 px-2 py-1 rounded-lg transition-colors">
                      <span className="text-rsb-text-secondary group-hover:text-white transition-colors">{label}</span>
                      <kbd className="px-2 py-1 bg-white/5 border border-white/10 rounded-md text-xs font-mono text-rsb-muted group-hover:text-white group-hover:border-white/20 transition-all">{shortcut}</kbd>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </ToastProvider>
  )
}
